import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import {
    EMAIL_INPUT,
    NAME_INPUT,
    MESSAGE_INPUT,
    PHONE_NUMBER_INPUT,
    ORGANISATION_INPUT,
    ADDRESS_INPUT,
} from '~/modules/validation/util/form';
import { z } from 'zod';
import {
    TextInput,
    pxArrayToRem,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './SampleEnquiryForm.style';
import React from 'react';
import type Product from '../../type/product';

export const enquiryFormValidator = z.object({
    Name: NAME_INPUT,
    Phone: PHONE_NUMBER_INPUT,
    Email: EMAIL_INPUT,
    Message: MESSAGE_INPUT,
    Company: ORGANISATION_INPUT,
    Address: ADDRESS_INPUT.optional(),
});

export const enquiryFormValidatorWithAddress = enquiryFormValidator.extend({
    Address: ADDRESS_INPUT,
});

export type EnquiryFormShape = z.infer<typeof enquiryFormValidator>;

const INPUT_DEFAULT_VARIABLES = {
    inputMarginLargeDesktop: pxArrayToRem([8, 16]),
    inputMarginMediumDesktop: pxArrayToRem([8, 16]),
    inputMarginMobile: pxArrayToRem([14, 13]),
    hoverBackgroundColor: 'transparent',
    minHeightLargeDesktop: pxToRem(48),
    minHeightMediumDesktop: pxToRem(48),
};
interface Props {
    data: Product;
    control: Control<EnquiryFormShape>;
}

const SampleEnquiryForm = ({
    data: {
        SampleAddressPlaceholder,
        SampleMessagePlaceholder,
        SamplesAddressRequired,
    },
    control,
}: Props) => {
    const { css } = useStyles();
    return (
        <div className={css(S.SampleEnquiryForm)}>
            <Controller
                control={control}
                name={'Name'}
                render={({ field, fieldState: { error } }) => (
                    <TextInput
                        input={{
                            ...field,
                            placeholder: 'Full Name*',
                            required: true,
                        }}
                        state={error?.message ? 'error' : 'default'}
                        message={error?.message}
                        variables={INPUT_DEFAULT_VARIABLES}
                    />
                )}
            />
            <Controller
                control={control}
                name={'Company'}
                render={({ field, fieldState: { error } }) => (
                    <TextInput
                        input={{
                            ...field,
                            placeholder: 'Organisation Name',
                        }}
                        state={error?.message ? 'error' : 'default'}
                        message={error?.message}
                        variables={INPUT_DEFAULT_VARIABLES}
                    />
                )}
            />
            <Controller
                control={control}
                name={'Address'}
                render={({ field, fieldState: { error } }) => (
                    <TextInput
                        input={{
                            ...field,
                            placeholder: (
                                (SampleAddressPlaceholder || 'Address') +
                                (SamplesAddressRequired ? '*' : '')
                            ).replace('**', '*'),
                        }}
                        state={error?.message ? 'error' : 'default'}
                        message={error?.message}
                        variables={INPUT_DEFAULT_VARIABLES}
                    />
                )}
            />
            <Controller
                control={control}
                name={'Email'}
                render={({ field, fieldState: { error } }) => (
                    <TextInput
                        input={{
                            ...field,
                            placeholder: 'Email*',
                            required: true,
                        }}
                        state={error?.message ? 'error' : 'default'}
                        message={error?.message}
                        variables={INPUT_DEFAULT_VARIABLES}
                    />
                )}
            />
            <Controller
                control={control}
                name={'Phone'}
                render={({ field, fieldState: { error } }) => (
                    <TextInput
                        input={{
                            ...field,
                            placeholder: 'Phone Number*',
                            required: true,
                        }}
                        state={error?.message ? 'error' : 'default'}
                        message={error?.message}
                        variables={INPUT_DEFAULT_VARIABLES}
                        autoExpandMode={'none'}
                    />
                )}
            />
            <Controller
                control={control}
                name={'Message'}
                render={({ field, fieldState: { error } }) => (
                    <TextInput
                        input={{
                            ...field,
                            placeholder: SampleMessagePlaceholder || 'Message',
                            required: true,
                            input: {
                                as: 'textarea',
                            },
                        }}
                        variables={{
                            ...INPUT_DEFAULT_VARIABLES,
                            minHeightLargeDesktop: pxToRem(123),
                            minHeightMediumDesktop: pxToRem(123),
                        }}
                        state={error?.message ? 'error' : 'default'}
                        message={error?.message}
                    />
                )}
            />
        </div>
    );
};

export default SampleEnquiryForm;
